import React, { useState, useEffect, useRef } from 'react';
import {
  FaSave,
  FaPlus,
  FaEdit,
  FaTrash,
  FaImages,
  FaCloudUploadAlt,
  FaSpinner,
  FaTimes,
  FaCheckCircle,
  FaExclamationCircle,
  FaFolder,
  FaCalendarAlt,
  FaImage,
  FaSearch,
  FaChevronDown,
} from 'react-icons/fa';
import axios from 'axios';
import { useAdmin } from '../context/AdminContext';
import API_BASE_URL, { MEDIA_BASE_URL } from '../../config';
import './SectionManager.css';
import './GalleryManager.css';

// 🎯 PURE JS CANVAS IMAGE COMPRESSOR
const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    if (file.size <= 500 * 1024) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }
            const compressedFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, '') + '.jpg',
              {
                type: 'image/jpeg',
                lastModified: Date.now(),
              }
            );
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const GalleryManager = () => {
  const { data, addItem, updateItem, deleteItem, loading } = useAdmin();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showcaseServices, setShowcaseServices] = useState([]);

  // 🎯 SEARCHABLE SELECT DROPDOWN STATES
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null); // बाहर क्लिक करने पर बंद करने के लिए

  // 🎯 Fetch all individual Sub-Service Titles dynamically from API
  useEffect(() => {
    const fetchShowcaseTitles = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const res = await axios.get(`${API_BASE_URL}/service-projects/`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const projects = res.data?.results || res.data || [];
        const projectTitles = projects.map((p) => p.title).filter(Boolean);

        const uniqueTitles = [...new Set(projectTitles)];
        setShowcaseServices(uniqueTitles);
      } catch (err) {
        console.error('Failed to load showcase titles for gallery dropdown', err);
        setShowcaseServices([
          'Home Tuition Services',
          'EdTech Learning Platform',
          'Admission Consultancy Agency',
          'Medical Store & Pharmacy',
          'Pharmaceutical Distribution Agency',
          'Multi-Specialty Hospital (Upcoming)',
          'Fly Ash Brick Manufacturing Plant',
        ]);
      }
    };
    fetchShowcaseTitles();
  }, [data?.services?.length]);

  const defaultCategory = showcaseServices[0] || 'Home Tuition Services';

  const [form, setForm] = useState({
    title: '',
    category: defaultCategory,
    customCategory: '',
    date: new Date().toISOString().split('T')[0],
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);
  const [sizeInfo, setSizeInfo] = useState({ original: '', compressed: '' });
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const galleryList = data?.gallery || [];

  useEffect(() => {
    if (showcaseServices.length > 0 && !editId) {
      setForm((prev) => ({ ...prev, category: showcaseServices[0] }));
    }
  }, [showcaseServices, editId]);

  // 🎯 DROP DOWN CLICK OUTSIDE EVENT LISTENER
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getCleanPreviewUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http') || img.startsWith('blob:')) return img;
    return `${MEDIA_BASE_URL}${img.startsWith('/') ? '' : '/'}${img}`;
  };

  const notifySuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMsg('Please select a valid image file!');
      return;
    }

    try {
      setIsCompressing(true);
      setErrorMsg('');
      const originalSizeFormatted = formatFileSize(file.size);
      const compressed = await compressImage(file);
      const compressedSizeFormatted = formatFileSize(compressed.size);

      setImageFile(compressed);
      setImagePreview(URL.createObjectURL(compressed));
      setSizeInfo({
        original: originalSizeFormatted,
        compressed: compressedSizeFormatted,
      });
    } catch (err) {
      console.error('Compression Error:', err);
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    } finally {
      setIsCompressing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    let finalCategory = form.category;
    if (form.category === 'Other') {
      if (!form.customCategory.trim()) {
        setErrorMsg('Please enter a custom category name!');
        return;
      }
      finalCategory = form.customCategory.trim();
    }

    try {
      const payload = {
        title: form.title.trim(),
        category: finalCategory,
        date: form.date,
        imageFile,
        imagePreview,
      };

      if (editId) {
        await updateItem('gallery', editId, payload);
        notifySuccess('Gallery asset updated successfully!');
      } else {
        await addItem('gallery', payload);
        notifySuccess('New photo added to gallery successfully!');
      }
      resetForm();
    } catch (err) {
      console.error('Error saving gallery item:', err);
      setErrorMsg('Failed to save asset. Please try again.');
    }
  };

  const handleEdit = (item) => {
    const itemCat = item.category || defaultCategory;
    const isShowcase = showcaseServices.some(
      (c) => c.toLowerCase() === itemCat.toLowerCase()
    );

    setForm({
      title: item.title || '',
      category: isShowcase ? item.category : 'Other',
      customCategory: isShowcase ? '' : item.category,
      date: item.date || new Date().toISOString().split('T')[0],
    });

    const activeImg = item.emoji || item.image || '';
    setImagePreview(getCleanPreviewUrl(activeImg));
    setImageFile(null);
    setSizeInfo({ original: '', compressed: '' });
    setEditId(item.id);
    setShowForm(true);
    setErrorMsg('');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gallery photo?')) {
      await deleteItem('gallery', id);
      notifySuccess('Photo deleted from gallery successfully.');
    }
  };

  const removeSelectedImage = () => {
    setImageFile(null);
    setImagePreview('');
    setSizeInfo({ original: '', compressed: '' });
    const input = document.getElementById('gallery-image-input');
    if (input) input.value = '';
  };

  const resetForm = () => {
    setForm({
      title: '',
      category: defaultCategory,
      customCategory: '',
      date: new Date().toISOString().split('T')[0],
    });
    setImageFile(null);
    setImagePreview('');
    setSizeInfo({ original: '', compressed: '' });
    setIsCompressing(false);
    setEditId(null);
    setShowForm(false);
    setErrorMsg('');
    setIsDropdownOpen(false);
    setSearchQuery('');
  };

  // 🎯 FILTER OPTIONS ACCORDING TO SEARCH KEYWORD
  const filteredServices = showcaseServices.filter((service) =>
    service.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryBadgeClass = (cat = '') => {
    const c = cat.toLowerCase();
    if (c.includes('tuition') || c.includes('education') || c.includes('edtech')) return 'sg-tag-education';
    if (c.includes('pharmacy') || c.includes('medical') || c.includes('hospital')) return 'sg-tag-healthcare';
    if (c.includes('brick') || c.includes('construction') || c.includes('plant')) return 'sg-tag-construction';
    return 'sg-tag-custom';
  };

  if (loading && galleryList.length === 0) {
    return (
      <div className="sg-manager">
        <div className="gl-loader">
          <FaSpinner className="spin" />
          <p>Loading gallery assets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sg-manager">
      {/* PAGE HEADER */}
      <div className="sg-page-header">
        <div className="sg-ph-left">
          <div className="sg-ph-icon">
            <FaImages />
          </div>
          <div className="sg-ph-text">
            <h2>Gallery Manager</h2>
            <p>Upload corporate photos, categorize assets, and showcase division services</p>
          </div>
        </div>
        <div className="sg-ph-right">
          <button className="sg-btn-gold" onClick={() => setShowForm(!showForm)}>
            {showForm ? (
              <>
                <FaTimes /> Close Form
              </>
            ) : (
              <>
                <FaPlus /> Add Gallery Item
              </>
            )}
          </button>
        </div>
      </div>

      {/* Action Alerts */}
      {successMsg && (
        <div className="sg-alert sg-alert-success">
          <FaCheckCircle /> {successMsg}
        </div>
      )}

      {errorMsg && (
        <div className="sg-alert sg-alert-error">
          <FaExclamationCircle /> {errorMsg}
        </div>
      )}

      {/* FORM CONTAINER */}
      {showForm && (
        <div className="sg-form-card">
          <div className="sg-form-header">
            <div>
              <h3>{editId ? 'Modify Visual Asset' : 'Add New Photo'}</h3>
              <p>Enter details and upload high-quality asset for web portfolio</p>
            </div>
            <button className="sg-form-close" onClick={resetForm}>
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="sg-form">
            <div className="gl-form-row">
              {/* Title */}
              <div className="sg-field">
                <label>
                  Photo Title <span className="req">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Home Tuition Session Raipur"
                  required
                />
              </div>

              {/* 🎯 SEARCHABLE DROPDOWN CHANGER SECTION */}
              <div className="sg-field sg-search-dropdown-field" ref={dropdownRef}>
                <label>
                  Division Service / Portfolio <span className="req">*</span>
                </label>
                <div 
                  className={`sg-dropdown-trigger ${isDropdownOpen ? 'active' : ''}`}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span className="sg-dt-value">{form.category || 'Select Service...'}</span>
                  <FaChevronDown className="sg-dt-arrow" />
                </div>

                {isDropdownOpen && (
                  <div className="sg-dropdown-menu">
                    {/* Internal Search Bar inside Select Options */}
                    <div className="sg-dropdown-search">
                      <FaSearch className="sg-ds-icon" />
                      <input
                        type="text"
                        placeholder="Type to filter..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()} // Click होने पर ड्रॉपडाउन बंद होने से रोके
                        autoFocus
                      />
                      {searchQuery && (
                        <button 
                          type="button" 
                          className="sg-ds-clear-btn"
                          onClick={(e) => { e.stopPropagation(); setSearchQuery(''); }}
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>

                    {/* Options List */}
                    <ul className="sg-dropdown-list">
                      {filteredServices.length === 0 ? (
                        <li className="sg-dropdown-no-results">No matches found</li>
                      ) : (
                        filteredServices.map((title) => (
                          <li
                            key={title}
                            className={`sg-dropdown-item ${form.category === title ? 'selected' : ''}`}
                            onClick={() => {
                              setForm({ ...form, category: title });
                              setIsDropdownOpen(false);
                              setSearchQuery('');
                            }}
                          >
                            {title}
                          </li>
                        ))
                      )}
                      
                      {/* Standard Custom Other Category Toggle */}
                      <li className="sg-dropdown-divider"></li>
                      <li
                        className={`sg-dropdown-item sg-dropdown-item-custom ${form.category === 'Other' ? 'selected' : ''}`}
                        onClick={() => {
                          setForm({ ...form, category: 'Other' });
                          setIsDropdownOpen(false);
                          setSearchQuery('');
                        }}
                      >
                        ✨ Other (Custom category)...
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Custom Category / Date Switch */}
              {form.category === 'Other' ? (
                <div className="sg-field" style={{ animation: 'sg-fadeIn 0.3s' }}>
                  <label>
                    Custom Category Name <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.customCategory}
                    onChange={(e) =>
                      setForm({ ...form, customCategory: e.target.value })
                    }
                    placeholder="e.g. Festival, Awards"
                    required
                  />
                </div>
              ) : (
                <div className="sg-field">
                  <label>
                    Capture Date <span className="req">*</span>
                  </label>
                  <div className="sg-input-icon-group">
                    <FaCalendarAlt className="sg-field-inner-icon" />
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      required
                      style={{ paddingLeft: '40px' }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Shift date field below if custom category active */}
            {form.category === 'Other' && (
              <div className="sg-field gl-date-shifted-field">
                <label>
                  Capture Date <span className="req">*</span>
                </label>
                <div className="sg-input-icon-group">
                  <FaCalendarAlt className="sg-field-inner-icon" />
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    required
                    style={{ paddingLeft: '40px' }}
                  />
                </div>
              </div>
            )}

            {/* Image Upload Area with Compression */}
            <div className="sg-field">
              <label>
                Photo (Optional)
              </label>
              <div className={`sg-upload-box ${imagePreview ? 'has-image' : ''}`}>
                {imagePreview ? (
                  <div className="sg-upload-preview gl-preview-grid">
                    <img src={imagePreview} alt="Preview" className="sg-preview-img" />
                    <div className="sg-upload-actions">
                      <label className="sg-upload-change">
                        {isCompressing ? (
                          <>
                            <FaSpinner className="spin" /> Compressing...
                          </>
                        ) : (
                          <>
                            <FaCloudUploadAlt /> Change Asset
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          style={{ display: 'none' }}
                          id="gallery-image-input"
                          disabled={isCompressing}
                        />
                      </label>
                      <button
                        type="button"
                        className="sg-upload-remove"
                        onClick={removeSelectedImage}
                        disabled={isCompressing}
                      >
                        <FaTimes /> Remove Selected
                      </button>

                      {sizeInfo.compressed && (
                        <div className="sg-size-info">
                          <FaCheckCircle /> Optimized: {sizeInfo.original} →{' '}
                          {sizeInfo.compressed}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <label className="sg-upload-empty">
                    {isCompressing ? (
                      <FaSpinner className="spin sg-upload-icon" />
                    ) : (
                      <FaImages className="sg-upload-icon" />
                    )}
                    <span className="sg-upload-title">
                      {isCompressing ? 'Compressing Image...' : 'Click to Upload Asset'}
                    </span>
                    <span className="sg-upload-hint">
                      JPG, PNG, WEBP • Auto Optimization Active
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                      id="gallery-image-input"
                      disabled={isCompressing}
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="sg-form-actions">
              <button
                type="submit"
                className="sg-btn-gold gl-save-btn"
                disabled={isCompressing}
              >
                <FaSave /> {editId ? 'Update Asset' : 'Upload Asset'}
              </button>
              <button
                type="button"
                className="sg-btn-outline gl-cancel-btn"
                onClick={resetForm}
                disabled={isCompressing}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* DATA VIEW (TABLE LIST) */}
      <div className="sg-table-card">
        <div className="sg-table-header">
          <div>
            <h3>Visual Portfolio Repository</h3>
            <p>Review and edit visual media assets across active verticals</p>
          </div>
          <span className="sg-count-badge">{galleryList.length} Total Assets</span>
        </div>

        <div className="sg-table-wrap">
          <table className="sg-table">
            <thead>
              <tr>
                <th style={{ width: '100px' }}>Thumbnail</th>
                <th>Title</th>
                <th style={{ width: '150px' }}>Division Service</th>
                <th style={{ width: '150px' }}>Capture Date</th>
                <th style={{ width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {galleryList.length === 0 ? (
                <tr>
                  <td colSpan="5" className="sg-empty">
                    <div className="sg-empty-inner">
                      <FaImage />
                      <h4>No Visual Assets Registered</h4>
                      <p>Click "Add Gallery Item" above to upload your first corporate photo</p>
                    </div>
                  </td>
                </tr>
              ) : (
                galleryList.map((item, index) => (
                  <tr key={item.id || `gallery-${index}`}>
                    <td data-label="Thumbnail">
                      <div className="gl-thumb-frame">
                        {item.emoji || item.image ? (
                          <img
                            src={getCleanPreviewUrl(item.emoji || item.image)}
                            alt={item.title}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                'https://images.unsplash.com/photo-1542838132-92c53300491e?w=200';
                            }}
                          />
                        ) : (
                          <div className="gl-thumb-placeholder">
                            <FaImage />
                          </div>
                        )}
                      </div>
                    </td>
                    <td data-label="Title">
                      <strong className="sg-row-title">{item.title || 'Untitled Asset'}</strong>
                    </td>
                    <td data-label="Division Service">
                      <span className={`sg-tag ${getCategoryBadgeClass(item.category)}`}>
                        <FaFolder /> {item.category || 'Other'}
                      </span>
                    </td>
                    <td data-label="Capture Date">
                      <span className="gl-date-badge">
                        <FaCalendarAlt /> {item.date || '—'}
                      </span>
                    </td>
                    <td data-label="Actions">
                      <div className="sg-actions">
                        <button
                          className="sg-action-btn sg-edit"
                          onClick={() => handleEdit(item)}
                          title="Edit Asset"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="sg-action-btn sg-delete"
                          onClick={() => handleDelete(item.id)}
                          title="Delete Asset"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GalleryManager;